import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge, Box, Button, Divider, IconButton, Popover,
  Stack, Tooltip, Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CircleIcon from '@mui/icons-material/Circle';
import { useAuthStore } from '@/store/auth';
import {
  useMarkAllNotificationsRead, useMarkNotificationRead,
  useNotifications, type Notification,
} from '@/api/notifications';
import { APP_BASE } from '@/lib/api-base';
// Keeps navigation inside the app (the old safeInAppPath guard, CVE-2026-53669)
// and adds the URL prefix the router's absolute routes need.
import { toAppPath } from '@/lib/app-link';

/**
 * Topbar bell icon. Polls /api/notifications every 30s for the signed-in
 * user (their user_id AND role — backend returns the union). Badge shows
 * the unread count; popover lists rows with one-click "open + mark read".
 *
 * Both employees and HR see this — the recipient field on each row
 * decides who sees what. HR sees fan-out role-targeted rows
 * (DECLARATION_SUBMITTED for the whole HR role); employees see direct
 * user-id-targeted rows (DECLARATION_APPROVED for their login).
 */
export function NotificationBell() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const userId = user?.id ?? null;
  const role   = user?.role ?? null;

  // Poll the API in the background regardless of popover state — we want
  // the badge to update even while the user is in another tab. Drop to a
  // slower 60s pulse when the popover is closed; 5s while it's open so
  // dismissals feel instant.
  const q = useNotifications(
    { user_id: userId, role: role, status: 'UNREAD' },
    anchor ? 5_000 : 30_000,
  );
  const markRead    = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const items = q.data?.items ?? [];
  const unread = q.data?.unread_count ?? 0;

  const onRowClick = (n: Notification) => {
    setAnchor(null);
    if (n.status === 'PENDING') markRead.mutate(n.id);
    // Producers store `/app/x`, `/x` or the prefixed path; the router has no
    // basename, so it needs the full prefixed path. See lib/app-link.
    if (n.link_path) navigate(toAppPath(n.link_path));
  };

  if (!userId && !role) return null;

  return (
    <>
      <Tooltip title={unread ? `${unread} unread notification${unread === 1 ? '' : 's'}` : 'Notifications'}>
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} sx={{ ml: 0.5 }}>
          <Badge badgeContent={unread} color="error" max={99}
            slotProps={{ badge: { sx: { fontSize: 9, height: 16, minWidth: 16 } } }}>
            <NotificationsIcon fontSize="small" sx={{ color: unread ? 'primary.main' : 'text.secondary' }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxHeight: 480 } } }}
      >
        <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 1 }}>
          <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700 }}>
            Notifications
            {unread > 0 && (
              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'primary.main' }}>
                {unread} unread
              </Typography>
            )}
          </Typography>
          {unread > 0 && (
            <Button size="small" onClick={() => markAllRead.mutate({ user_id: userId, role })}
              disabled={markAllRead.isPending}>
              Mark all read
            </Button>
          )}
        </Stack>
        <Divider />

        {items.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">No new notifications.</Typography>
            <Typography variant="caption">
              You're all caught up.
            </Typography>
          </Box>
        )}

        {items.map((n) => (
          <Box key={n.id}
            onClick={() => onRowClick(n)}
            sx={{
              px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider',
              cursor: n.link_path ? 'pointer' : 'default',
              bgcolor: n.status === 'PENDING' ? 'action.hover' : 'background.paper',
              '&:hover': n.link_path ? { bgcolor: 'action.selected' } : undefined,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              {n.status === 'PENDING' && (
                <CircleIcon sx={{ fontSize: 8, color: 'primary.main', mt: 0.75, flexShrink: 0 }} />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{
                  fontWeight: n.status === 'PENDING' ? 700 : 500,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {n.subject}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{
                  // Use the multi-line clamp display (overrides earlier `block`)
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  whiteSpace: 'pre-wrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {n.body}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
                  {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                  &nbsp;·&nbsp;{n.kind.replace(/_/g, ' ').toLowerCase()}
                </Typography>
              </Box>
            </Stack>
          </Box>
        ))}
        <Divider />
        <Box sx={{ px: 1.5, py: 0.75 }}>
          <Button size="small" fullWidth
            onClick={() => {
              setAnchor(null);
              // Employees are confined to /me (AppShell), so their history lives there.
              navigate(`${APP_BASE}${role === 'EMPLOYEE' ? '/me' : ''}/notifications`);
            }}>
            View all notifications
          </Button>
        </Box>
      </Popover>
    </>
  );
}
